import { slicedToArray as _slicedToArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useState, useEffect } from 'react';
import { Box, Spacer, Heading, Text, Paragraph, TextButton, IconChevronUp, IconChevronDown, Anchor } from '@zillow/constellation';
import TextField from '../Fields/TextField.js';
import { commaSeparateNumber } from '../../utils/zhl-calculator.js';
import { useLastValidClosingCostCalculator, FieldConfig } from './ClosingCostCalculator.utils.js';

var ClosingCostContent = function ClosingCostContent() {
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    showBreakdownCost = _useState2[0],
    setShowBreakdownCost = _useState2[1];
  var _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    closingCost = _useState4[0],
    setClosingCost = _useState4[1];
  var _useState5 = useState(0),
    _useState6 = _slicedToArray(_useState5, 2),
    closingCostPercentage = _useState6[0],
    setClosingCostPercentage = _useState6[1];
  var lastValidClosingCostCalculator = useLastValidClosingCostCalculator();
  useEffect(function () {
    var _lastValidClosingCost = lastValidClosingCostCalculator.calculations,
      closingCost = _lastValidClosingCost.closingCost,
      closingCostPercentage = _lastValidClosingCost.closingCostPercentage;
    setClosingCost(closingCost);
    setClosingCostPercentage(closingCostPercentage);
  }, [lastValidClosingCostCalculator]);
  return /*#__PURE__*/React.createElement(Box, {
    sx: {
      margin: '$spacing.md',
      border: 'solid 1px $colors.borderLight',
      paddingBottom: '$spacing.md',
      '@media $sm_lte': {
        margin: '$spacing.xs'
      }
    }
  }, /*#__PURE__*/React.createElement(Box, {
    sx: {
      paddingTop: '$spacing.md',
      margin: 'auto',
      maxWidth: '$breakpoints.md'
    }
  }, /*#__PURE__*/React.createElement(Spacer, {
    style: {
      textAlign: 'center'
    }
  }, /*#__PURE__*/React.createElement(Heading, {
    level: "4",
    marginBottom: "xs",
    "aria-live": "polite",
    "aria-atomic": "true"
  }, "Your estimated closing cost is:", /*#__PURE__*/React.createElement(Text, {
    style: {
      display: 'block'
    },
    fontType: "h2",
    fontColor: "green500",
    margin: "md"
  }, "$", commaSeparateNumber(Math.round(closingCost)))), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Paragraph, {
    style: {
      margin: '25px'
    }
  }, "Closing costs would be", ' ', /*#__PURE__*/React.createElement("b", null, closingCostPercentage, "%"), " of your purchase price. Get a more accurate closing cost estimate by adjusting fees to fit your situation."))), /*#__PURE__*/React.createElement(Spacer, {
    style: {
      textAlign: 'center'
    }
  }, /*#__PURE__*/React.createElement(TextButton, {
    icon: showBreakdownCost ? /*#__PURE__*/React.createElement(IconChevronUp, null) : /*#__PURE__*/React.createElement(IconChevronDown, null),
    iconSize: "xs",
    iconPosition: "end",
    type: "button",
    onClick: function onClick() {
      setShowBreakdownCost(!showBreakdownCost);
    }
  }, showBreakdownCost ? 'Hide breakdown of costs' : 'Show breakdown of costs'))), showBreakdownCost && /*#__PURE__*/React.createElement(Box, {
    sx: {
      margin: '$spacing.md'
    }
  }, /*#__PURE__*/React.createElement(Box, {
    sx: {
      paddingBottom: '$spacing.sm'
    }
  }, /*#__PURE__*/React.createElement(Heading, {
    level: "5",
    marginBottom: "sm",
    "aria-live": "polite",
    "aria-atomic": "true"
  }, "Loan fees"), /*#__PURE__*/React.createElement(Box, {
    sx: {
      display: 'flex',
      flexWrap: 'wrap',
      columnGap: '$spacing.md'
    }
  }, /*#__PURE__*/React.createElement(Box, {
    sx: {
      width: 'calc(50% - 16px)',
      '@media $sm_lte': {
        width: '100%'
      }
    }
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "Loan origination fee",
    name: FieldConfig.loanOriginationFee.name,
    config: FieldConfig.loanOriginationFee.config,
    leftAdornment: "$",
    autoComplete: "off",
    format: commaSeparateNumber,
    tooltip: {
      title: 'Loan origination fee',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "This fee covers the lender's administrative cost associated with mortgage application and processing, typically 0.5% to 1% of the loan amount. The calculator assumes that lender fees amount to 0.5% of the loan amount.")
    }
  })), /*#__PURE__*/React.createElement(Box, {
    sx: {
      width: 'calc(50% - 16px)',
      '@media $sm_lte': {
        width: '100%'
      }
    }
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "Credit report fee",
    name: FieldConfig.creditReportFee.name,
    config: FieldConfig.creditReportFee.config,
    autoComplete: "off",
    leftAdornment: "$",
    format: commaSeparateNumber,
    tooltip: {
      title: 'Credit report fee',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "This is a fee charged by the lender to pull your credit report. Generally credit report fees are around $35 but may be higher depending on the type of credit report a lender orders.")
    }
  })), /*#__PURE__*/React.createElement(Box, {
    sx: {
      width: 'calc(50% - 16px)',
      '@media $sm_lte': {
        width: '100%'
      }
    }
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "Discount points",
    name: FieldConfig.discountPoints.name,
    config: FieldConfig.discountPoints.config,
    leftAdornment: "$",
    autoComplete: "off",
    format: commaSeparateNumber,
    tooltip: {
      title: 'Discount points',
      body: /*#__PURE__*/React.createElement(Paragraph, null, /*#__PURE__*/React.createElement(Anchor, {
        href: "https://www.zillow.com/learn/mortgage-points/",
        target: "_blank"
      }, "Discount points"), ' ', "(mortgage points) are fees paid upfront to reduce the loan's interest rate. Each mortgage discount point will cost you 1% of the loan amount and cut your interest rate by 0.25%.")
    }
  })))), /*#__PURE__*/React.createElement(Box, {
    sx: {
      paddingBottom: '$spacing.sm'
    }
  }, /*#__PURE__*/React.createElement(Heading, {
    level: "5",
    marginBottom: "sm",
    "aria-live": "polite",
    "aria-atomic": "true"
  }, "Title and legal fees"), /*#__PURE__*/React.createElement(Box, {
    sx: {
      display: 'flex',
      flexWrap: 'wrap',
      columnGap: '$spacing.md'
    }
  }, /*#__PURE__*/React.createElement(Box, {
    sx: {
      width: 'calc(50% - 16px)',
      '@media $sm_lte': {
        width: '100%'
      }
    }
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "Title search fee",
    name: FieldConfig.titleSearchFee.name,
    config: FieldConfig.titleSearchFee.config,
    leftAdornment: "$",
    autoComplete: "off",
    format: commaSeparateNumber,
    tooltip: {
      title: 'Title search fee',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "This is a fee paid to a title or escrow company to research the property's ownership history to ensure clear title.")
    }
  })), /*#__PURE__*/React.createElement(Box, {
    sx: {
      width: 'calc(50% - 16px)',
      '@media $sm_lte': {
        width: '100%'
      }
    }
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "Title insurance",
    name: FieldConfig.titleInsurance.name,
    config: FieldConfig.titleInsurance.config,
    leftAdornment: "$",
    autoComplete: "off",
    format: commaSeparateNumber,
    tooltip: {
      title: 'Title insurance',
      body: /*#__PURE__*/React.createElement(Paragraph, null, /*#__PURE__*/React.createElement(Anchor, {
        href: "https://www.zillow.com/learn/what-is-title-insurance/",
        target: "_blank"
      }, "Title insurance"), ' ', "is a type of insurance that protects mortgage lenders and/or homeowners against claims questioning the legal ownership of a home or property. Cost varies by property value and location. The calculator assumes that these fees amount to 0.5% of the home price.")
    }
  })), /*#__PURE__*/React.createElement(Box, {
    sx: {
      width: 'calc(50% - 16px)',
      '@media $sm_lte': {
        width: '100%'
      }
    }
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "Attorney fees",
    name: FieldConfig.attorneyFees.name,
    config: FieldConfig.attorneyFees.config,
    leftAdornment: "$",
    autoComplete: "off",
    format: commaSeparateNumber,
    tooltip: {
      title: 'Attorney fees',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "In states requiring a closing attorney, costs range from $500 to $1,500, split between buyer and seller. Attorneys are required to oversee closing in 21 states and Washington, D.C. Private real estate attorneys, or borrower's attorneys, are an additional and optional cost for buyers who want a specialist to assist them with contract-related issues or professional advice.")
    }
  })), /*#__PURE__*/React.createElement(Box, {
    sx: {
      width: 'calc(50% - 16px)',
      '@media $sm_lte': {
        width: '100%'
      }
    }
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "Settlement or escrow fee",
    name: FieldConfig.settelementOrEscrowFee.name,
    config: FieldConfig.settelementOrEscrowFee.config,
    leftAdornment: "$",
    autoComplete: "off",
    format: commaSeparateNumber,
    tooltip: {
      title: 'Settlement or escrow fee',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "This fee is paid to the title or escrow company handling the closing as a neutral third party. They hold funds during the transaction and disburse payments upon closing. In states requiring attorney-led closings (e.g., CT, DE, GA, MA, NY, SC, WV), attorneys handle this role. Fees range depending on your location. The cost is typically split evenly between the buyer and seller, but this must be negotiated and detailed in the contract. This calculator assumes that these fees are around 0.25% of the home purchase price.")
    }
  })))), /*#__PURE__*/React.createElement(Box, {
    sx: {
      paddingBottom: '$spacing.sm'
    }
  }, /*#__PURE__*/React.createElement(Heading, {
    level: "5",
    marginBottom: "sm",
    "aria-live": "polite",
    "aria-atomic": "true"
  }, "Prepaid expenses"), /*#__PURE__*/React.createElement(Box, {
    sx: {
      display: 'flex',
      flexWrap: 'wrap',
      columnGap: '$spacing.md'
    }
  }, /*#__PURE__*/React.createElement(Box, {
    sx: {
      width: 'calc(50% - 16px)',
      '@media $sm_lte': {
        width: '100%'
      }
    }
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "Homeowners insurance",
    name: FieldConfig.homeownerInsurance.name,
    config: FieldConfig.homeownerInsurance.config,
    leftAdornment: "$",
    autoComplete: "off",
    format: commaSeparateNumber,
    tooltip: {
      title: 'Homeowners insurance',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "Lenders usually require homeowners insurance before closing. Homeowners insurance policy fees range based on the amount of coverage and the size of the property. This calculator assumes the yearly premium to be 0.35% of the home's price.")
    }
  })), /*#__PURE__*/React.createElement(Box, {
    sx: {
      width: 'calc(50% - 16px)',
      '@media $sm_lte': {
        width: '100%'
      }
    }
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "Property taxes",
    name: FieldConfig.propertyTaxes.name,
    config: FieldConfig.propertyTaxes.config,
    autoComplete: "off",
    leftAdornment: "$",
    format: commaSeparateNumber,
    tooltip: {
      title: 'Property taxes',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "Prorated shares of the annual property tax may be required from both the buyer and seller. Some buyers pay their taxes in lump sums annually or biannually. The calculator assumes 3 months of property taxes.")
    }
  })), /*#__PURE__*/React.createElement(Box, {
    sx: {
      width: 'calc(50% - 16px)',
      '@media $sm_lte': {
        width: '100%'
      }
    }
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "Prepaid interest",
    name: FieldConfig.prepaidInterest.name,
    config: FieldConfig.prepaidInterest.config,
    autoComplete: "off",
    leftAdornment: "$",
    format: commaSeparateNumber,
    tooltip: {
      title: 'Prepaid interest',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "You will be responsible for pre-paying any interest that will accrue on your home between the closing date and your first mortgage payment. Your lender will calculate the cost using the daily interest rate, multiplied by the number of days. The calculator assumes 15 days of prepaid interest.")
    }
  })))), /*#__PURE__*/React.createElement(Box, {
    sx: {
      paddingBottom: '$spacing.sm'
    }
  }, /*#__PURE__*/React.createElement(Heading, {
    level: "5",
    marginBottom: "sm",
    "aria-live": "polite",
    "aria-atomic": "true"
  }, "Inspection and appraisal fees"), /*#__PURE__*/React.createElement(Box, {
    sx: {
      display: 'flex',
      flexWrap: 'wrap',
      columnGap: '$spacing.md'
    }
  }, /*#__PURE__*/React.createElement(Box, {
    sx: {
      width: 'calc(50% - 16px)',
      '@media $sm_lte': {
        width: '100%'
      }
    }
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "Appraisal fee",
    name: FieldConfig.appraisalFee.name,
    config: FieldConfig.appraisalFee.config,
    leftAdornment: "$",
    autoComplete: "off",
    format: commaSeparateNumber,
    tooltip: {
      title: 'Appraisal fee',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "A", ' ', /*#__PURE__*/React.createElement(Anchor, {
        href: "https://www.zillow.com/learn/home-appraisal/",
        target: "_blank"
      }, "home appraisal"), ' ', "fee is required by lenders to determine the home's fair market value.")
    }
  })), /*#__PURE__*/React.createElement(Box, {
    sx: {
      width: 'calc(50% - 16px)',
      '@media $sm_lte': {
        width: '100%'
      }
    }
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "Home inspection fee",
    name: FieldConfig.homeInspectionFee.name,
    config: FieldConfig.homeInspectionFee.config,
    autoComplete: "off",
    leftAdornment: "$",
    format: commaSeparateNumber,
    tooltip: {
      title: 'Home inspection fee',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "A", ' ', /*#__PURE__*/React.createElement(Anchor, {
        href: "https://www.zillow.com/learn/home-inspection/",
        target: "_blank"
      }, "home inspection"), ' ', "is a common contingency for a home purchase. As the buyer, you can hire an inspector to evaluate the condition of the home and its systems prior to purchase. Home inspection costs vary depending on the size of the property, usually around $250 to $700.")
    }
  })), /*#__PURE__*/React.createElement(Box, {
    sx: {
      width: 'calc(50% - 16px)',
      '@media $sm_lte': {
        width: '100%'
      }
    }
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "Pest inspection fee",
    name: FieldConfig.pestInspectionFee.name,
    config: FieldConfig.pestInspectionFee.config,
    autoComplete: "off",
    leftAdornment: "$",
    format: commaSeparateNumber,
    tooltip: {
      title: 'Pest inspection fee',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "This inspection is required on some government loans and by certain states. While some home inspectors include this service in their standard home inspection, many do not. You'll likely pay for a specialty inspection service.")
    }
  })), /*#__PURE__*/React.createElement(Box, {
    sx: {
      width: 'calc(50% - 16px)',
      '@media $sm_lte': {
        width: '100%'
      }
    }
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "Survey fee",
    name: FieldConfig.surveyFee.name,
    config: FieldConfig.surveyFee.config,
    autoComplete: "off",
    leftAdornment: "$",
    format: commaSeparateNumber,
    tooltip: {
      title: 'Survey fee',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "A fee paid to a surveyor to map the property boundaries. This verifies property lines, required in some states or by lenders. Fees will vary by lot size.")
    }
  })))), /*#__PURE__*/React.createElement(Box, {
    sx: {
      paddingBottom: '$spacing.sm'
    }
  }, /*#__PURE__*/React.createElement(Heading, {
    level: "5",
    marginBottom: "sm",
    "aria-live": "polite",
    "aria-atomic": "true"
  }, "Government fees"), /*#__PURE__*/React.createElement(Box, {
    sx: {
      display: 'flex',
      flexWrap: 'wrap',
      columnGap: '$spacing.md'
    }
  }, /*#__PURE__*/React.createElement(Box, {
    sx: {
      width: 'calc(50% - 16px)',
      '@media $sm_lte': {
        width: '100%'
      }
    }
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "Recording fee",
    name: FieldConfig.recordingFee.name,
    config: FieldConfig.recordingFee.config,
    autoComplete: "off",
    leftAdornment: "$",
    format: commaSeparateNumber,
    tooltip: {
      title: 'Recording fee',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "A government fee charged to record the transfer of ownership of the property.")
    }
  })))), /*#__PURE__*/React.createElement(Box, {
    sx: {
      paddingBottom: '$spacing.sm'
    }
  }, /*#__PURE__*/React.createElement(Heading, {
    level: "5",
    marginBottom: "sm",
    "aria-live": "polite",
    "aria-atomic": "true"
  }, "Additional fees"), /*#__PURE__*/React.createElement(Box, {
    sx: {
      display: 'flex',
      flexWrap: 'wrap',
      columnGap: '$spacing.md'
    }
  }, /*#__PURE__*/React.createElement(Box, {
    sx: {
      width: 'calc(50% - 16px)',
      '@media $sm_lte': {
        width: '100%'
      }
    }
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "Courier fee",
    name: FieldConfig.courierFee.name,
    config: FieldConfig.courierFee.config,
    autoComplete: "off",
    leftAdornment: "$",
    format: commaSeparateNumber,
    tooltip: {
      title: 'Courier fee',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "A fee charged for delivering documents related to the closing process.")
    }
  })), /*#__PURE__*/React.createElement(Box, {
    sx: {
      width: 'calc(50% - 16px)',
      '@media $sm_lte': {
        width: '100%'
      }
    }
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "Notary fee",
    name: FieldConfig.notaryFee.name,
    config: FieldConfig.notaryFee.config,
    autoComplete: "off",
    leftAdornment: "$",
    format: commaSeparateNumber,
    tooltip: {
      title: 'Notary fee',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "A fee paid to a notary public to notarize documents. Notaries charge by the signature, about $100 for closing paperwork but they can add fees for their travel.")
    }
  })), /*#__PURE__*/React.createElement(Box, {
    sx: {
      width: 'calc(50% - 16px)',
      '@media $sm_lte': {
        width: '100%'
      }
    }
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "HOA transfer fee",
    name: FieldConfig.hoaTransferFee.name,
    config: FieldConfig.hoaTransferFee.config,
    autoComplete: "off",
    leftAdornment: "$",
    format: commaSeparateNumber,
    tooltip: {
      title: 'HOA transfer fee',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "When you buy a property that is managed by a homeowners association (HOA), there is typically a transfer fee that covers changing the property owner. During the negotiation, you can detail which party will pay the transfer fee.")
    }
  })), /*#__PURE__*/React.createElement(Box, {
    sx: {
      width: 'calc(50% - 16px)',
      '@media $sm_lte': {
        width: '100%'
      }
    }
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "Flood certification fee",
    name: FieldConfig.floodCertificationFee.name,
    config: FieldConfig.floodCertificationFee.config,
    autoComplete: "off",
    leftAdornment: "$",
    format: commaSeparateNumber,
    tooltip: {
      title: 'Flood certification fee',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "Covers the cost of determining whether the property is located in a flood zone.")
    }
  })), /*#__PURE__*/React.createElement(Box, {
    sx: {
      width: 'calc(50% - 16px)',
      '@media $sm_lte': {
        width: '100%'
      }
    }
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "Bank processing fee",
    name: FieldConfig.bankProcessingFee.name,
    config: FieldConfig.bankProcessingFee.config,
    autoComplete: "off",
    leftAdornment: "$",
    format: commaSeparateNumber,
    tooltip: {
      title: 'Bank processing fee',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "A fee charged by the lender to cover administrative costs associated with processing your loan.")
    }
  }))))));
};
var ClosingCostContent$1 = ClosingCostContent;

export { ClosingCostContent$1 as default };
